import { Injectable, inject } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { WelcomeSplashComponent } from './welcome-splash.component';

@Injectable({ providedIn: 'root' })
export class WelcomeSplashService {
  private overlay = inject(Overlay);

  /**
   * Splash + navegacion suave:
   * fadeIn -> stay -> fadeOut (y navega mientras se desvanece)
   *
   * ✅ (1) Solo 1 vez por sesion (sessionStorage)
   * ✅ (2) Saludo por hora
   * ✅ Fecha/hora abajo
   * ✅ (3) Nombre con ellipsis (en CSS)
   */
  async showAndNavigate(
    navigateFn: () => Promise<any>,
    opts?: {
      userName?: string;
      logoSrc?: string;
      fadeInMs?: number;
      stayMs?: number;
      fadeOutMs?: number;
      sessionKey?: string; // por si quieres cambiar el key
    }
  ): Promise<void> {
    const key = opts?.sessionKey ?? 'rmd_welcome_shown';

    // ✅ (1) Solo una vez por sesion
    if (sessionStorage.getItem(key) === '1') {
      await navigateFn();
      return;
    }
    sessionStorage.setItem(key, '1');

    const fadeInMs  = opts?.fadeInMs ?? 650;
    const stayMs    = opts?.stayMs ?? 950;
    const fadeOutMs = opts?.fadeOutMs ?? 650;

    const ref = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'welcome-splash-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    const portal = new ComponentPortal(WelcomeSplashComponent);
    const cmpRef = ref.attach(portal);

    // Config logo
    cmpRef.instance.logoSrc = opts?.logoSrc ?? 'assets/images/logo1.png';

    // ✅ (2) saludo por hora
    const now = new Date();
    const saludo = this.getGreeting(now);
    const user = (opts?.userName ?? '').trim();

    cmpRef.instance.subtitle = user ? `${saludo}, ${user}` : saludo;

    // Fecha/hora en español (sin libs)
    cmpRef.instance.metaText = this.formatMeta(now);

    // ===== FASE 1: FADE IN =====
    cmpRef.instance.visible = true;
    await this.delay(fadeInMs);

    // ===== FASE 2: STAY =====
    await this.delay(stayMs);

    // ===== FASE 3: FADE OUT =====
    cmpRef.instance.visible = false;

    // Navega mientras se desvanece (evita el “golpe”)
    await navigateFn();

    // Espera a que termine el fade out
    await this.delay(fadeOutMs);

    ref.dispose();
  }

  private getGreeting(d: Date): string {
    const h = d.getHours();
    if (h < 12) return 'BUENOS DIAS';
    if (h < 19) return 'BUENAS TARDES';
    return 'BUENAS NOCHES';
  }

  private formatMeta(d: Date): string {
    const days = ['DOMINGO','LUNES','MARTES','MIERCOLES','JUEVES','VIERNES','SABADO'];
    const months = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

    const dd = String(d.getDate()).padStart(2, '0');
    const mm = months[d.getMonth()];
    const yyyy = d.getFullYear();

    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');

    return `${days[d.getDay()]} ${dd} ${mm} ${yyyy} · ${hh}:${min}`;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(res => setTimeout(res, ms));
  }
}
